import { useSyncExternalStore } from "react"
import { hashQueryKey, type QueryClient } from "@tanstack/react-query"
import { SUBSCRIPTION_STATUS_QUERY_KEY, type SubscriptionStatus } from "#/services/user/hooks/useSubscriptionStatus"

/**
 * Lets the dev tools make the app behave as though the signed-in member had a different
 * subscription status, by writing that status into the one React Query key the paywall reads.
 * Everything downstream — `useIsPaywalled`, `PaywallGate`, the `/paywall` reverse guard —
 * follows, because none of them know any other source.
 *
 * The same shape as the Dynamic Content Toggles' `useCreateOverride`
 * (src/components/dynamic-content/hooks/useGetBenefit.ts): `setQueryDefaults` to pin the entry,
 * then `setQueryData`. Plus the re-apply below, which that one does not need.
 *
 * ⚠ NOTHING HERE RUNS UNTIL A BUTTON IS PRESSED, and that is load-bearing rather than tidy.
 * `["GetSubscriptionStatus"]` is shared with the route gate, and a disabled observer still reads
 * the cache — so a write for a member the gate deliberately never asked about (an enterprise
 * member, or any member with the flag off) would hand it a walling verdict out of nowhere. The
 * dev page only offers the buttons once its subscription section is `ready`, which is exactly
 * the case where the gate is already reading this key itself. See useDevToolsUserSnapshot.
 *
 * ⚠ IN-MEMORY AND HOST-ONLY. A reload restores the real value, and the consumer package's
 * billing and checkout screens read their own key (`["consumer","subscription","status"]`,
 * packages/consumer/src/subscription/hooks/useSubscriptionData.ts) and go on showing the truth.
 * The page says both of those out loud rather than leaving them to be discovered.
 */

/** The four coarse values the BFF can answer with — derived, so a fifth cannot be missed here. */
export type ForcedSubscriptionStatus = SubscriptionStatus["status"]

export type SubscriptionStatusOverride = {
  /** The status the app is being made to read. */
  forced: ForcedSubscriptionStatus
  /** What the cache held when the override was first applied, so the page can show both. */
  realStatus: ForcedSubscriptionStatus | null
}

type ActiveOverride = {
  snapshot: SubscriptionStatusOverride
  /** The exact object in the cache, so a re-apply is idempotent rather than a fresh write. */
  forcedValue: SubscriptionStatus
  /** Restored on clear, so dropping the override does not leave the cache empty. */
  realValue: SubscriptionStatus | undefined
  stopReapplying: () => void
}

let active: ActiveOverride | null = null
const listeners = new Set<() => void>()

function emit(): void {
  listeners.forEach((listener) => listener())
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

function getSnapshot(): SubscriptionStatusOverride | null {
  return active?.snapshot ?? null
}

/** Which status is being forced right now, and what the real one was. Null when nothing is. */
export function useSubscriptionStatusOverride(): SubscriptionStatusOverride | null {
  return useSyncExternalStore(subscribe, getSnapshot)
}

/**
 * Re-writes the forced value whenever anything overwrites it.
 *
 * ⚠ `staleTime: Infinity` ALONE IS NOT ENOUGH, which is the whole reason this exists. It stops
 * the refetches that are decided by staleness — on mount, and the window-focus re-read
 * `useSubscriptionStatus` turns on for every member who is not `active` — but an explicit
 * `invalidateQueries` marks the entry invalidated and refetches regardless of staleTime, and
 * `ConsumerSubscriptionBillingPage` fires one as it unmounts. That page is two clicks from
 * `/paywall`, so an override without this would evaporate on the most likely route anybody
 * testing the paywall takes, silently and with the page still claiming it was in force.
 *
 * Skips our own writes: `queryClient.setQueryData` dispatches `success` with `manual: true`,
 * so this cannot drive itself in a loop. A refetch that lands still reaches the observers for
 * one tick before it is corrected — the alternative is cancelling in-flight fetches from a
 * cache listener, which is a lot of machinery to spend on a flicker in a dev tool.
 */
function reapplyOnOverwrite(queryClient: QueryClient): () => void {
  const queryHash = hashQueryKey(SUBSCRIPTION_STATUS_QUERY_KEY)

  return queryClient.getQueryCache().subscribe((event) => {
    if (active === null || event.type !== "updated" || event.query.queryHash !== queryHash) return
    // `error` as well as `success`: a failed refetch leaves the data in place but flips the
    // query to error, and `useIsPaywalled` fails open on that — the override would look as
    // though it had been lifted. Re-applying puts the entry back to a settled success.
    if (event.action.type === "error") {
      queryClient.setQueryData(SUBSCRIPTION_STATUS_QUERY_KEY, active.forcedValue)
      return
    }
    if (event.action.type !== "success" || event.action.manual === true) return
    queryClient.setQueryData(SUBSCRIPTION_STATUS_QUERY_KEY, active.forcedValue)
  })
}

/**
 * Make the app read `status` for the signed-in member until it is cleared or the page reloads.
 *
 * Re-forcing keeps the ORIGINAL real value rather than capturing the forced one it replaces —
 * otherwise two presses would make "clear" restore a status the member never had.
 */
export function forceSubscriptionStatus(queryClient: QueryClient, status: ForcedSubscriptionStatus): void {
  const realValue = active
    ? active.realValue
    : queryClient.getQueryData<SubscriptionStatus>(SUBSCRIPTION_STATUS_QUERY_KEY)
  active?.stopReapplying()

  // Typed as the procedure's own return, so a field the BFF starts requiring fails to compile
  // here rather than reaching `useIsPaywalled` as a hand-shaped object it reads differently.
  // The optional period/amount fields are left off deliberately: nothing in the host reads
  // them, and carrying `currentPeriodEnd` from a real `active` onto a forced `none` would be
  // a combination the BFF never produces.
  const forcedValue: SubscriptionStatus = { hasSubscription: status !== "none", status }

  queryClient.setQueryDefaults(SUBSCRIPTION_STATUS_QUERY_KEY, { staleTime: Infinity, cacheTime: Infinity })
  active = {
    snapshot: { forced: status, realStatus: realValue?.status ?? null },
    forcedValue,
    realValue,
    stopReapplying: reapplyOnOverwrite(queryClient)
  }
  queryClient.setQueryData(SUBSCRIPTION_STATUS_QUERY_KEY, forcedValue)
  emit()
}

/**
 * Drop the override and go back to the real status.
 *
 * Puts the pre-override value back BEFORE invalidating, because the invalidation only refetches
 * for an observer that has the query enabled — and if none does, an entry left holding the
 * forced value would be a "cleared" override that is still in force.
 */
export function clearSubscriptionStatusOverride(queryClient: QueryClient): void {
  if (active === null) return

  const { realValue } = active
  active.stopReapplying()
  active = null

  queryClient.setQueryDefaults(SUBSCRIPTION_STATUS_QUERY_KEY, {})
  if (realValue === undefined) {
    queryClient.removeQueries({ queryKey: SUBSCRIPTION_STATUS_QUERY_KEY, exact: true })
  } else {
    queryClient.setQueryData(SUBSCRIPTION_STATUS_QUERY_KEY, realValue)
  }
  void queryClient.invalidateQueries({ queryKey: SUBSCRIPTION_STATUS_QUERY_KEY, exact: true })
  emit()
}
